import React, {useState, useEffect, useRef} from 'react';
import {Tooltip} from "primereact/tooltip";
import {useNavigate, useParams} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {ConfirmDialog} from "primereact/confirmdialog";
import {addLocale, locale} from "primereact/api";
import {InputText} from "primereact/inputtext";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";

import HelpComponent from "../HelpComponent";

const _FM_AccommodationListComponent = (props) => {
  const {t} = props;
  const navigate = useNavigate();
  const {id, action, entity} = useParams();
  const [accommodation, setAccommodation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hotelConnectRegistrationStatusByAccommodationId, setHotelConnectRegistrationStatusByAccommodationId] = useState({});
  const [hotelConnectRegistrationStatusLoadingByAccommodationId, setHotelConnectRegistrationStatusLoadingByAccommodationId] = useState({});
  const [hotelConnectSyncLoadingById, setHotelConnectSyncLoadingById] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState(10);
  const dt = useRef(null);
  const syncPollTimeoutsRef = useRef({});
  const headers = {
    'Authorization': 'Bearer ' + Cookies.get("userToken"),
    "Sys-name": Cookies.get("instanceId"),
    "Preferred-locale": Cookies.get("language")
  };
  const filterTextMatchModes = [
    {label: t('filter_like'), value: 'like'},
    {label: t('filter_equals'), value: '='},
    {label: t('filter_not_equals'), value: '!='},
  ]
  const filterNumberMatchModes = [
    {label: t('filter_between_number'), value: 'between'},
    {label: t('filter_equals'), value: '='},
    {label: t('filter_not_equals'), value: '!='},
    {label: t('filter_greather_than'), value: '>'},
    {label: t('filter_greather_than_or_equals'), value: '>='},
    {label: t('filter_less_than'), value: '<'},
    {label: t('filter_less_than_or_equals'), value: '<='},
  ]
  const filterDateMatchModes = [
    {label: t('filter_between_date'), value: 'between'},
    {label: t('filter_equals'), value: '='},
    {label: t('filter_not_equals'), value: '!='},
    {label: t('filter_greather_than'), value: '>'},
    {label: t('filter_greather_than_or_equals'), value: '>='},
    {label: t('filter_less_than'), value: '<'},
    {label: t('filter_less_than_or_equals'), value: '<='},
  ]
  const [lazyParams, setLazyParams] = useState({
    first: Number(localStorage.getItem('accommodation_first')) || 0,
    rows: Number(localStorage.getItem('accommodation_rows')) || rows,
    page: Number(localStorage.getItem('accommodation_page')) || 0,
    sortField: localStorage.getItem('accommodation_sortField') || 'id',
    sortOrder: Number(localStorage.getItem('accommodation_sortOrder')) || -1,
    filters: JSON.parse(localStorage.getItem('accommodation_filters')) || null,
  });
  let loadLazyTimeout = null;
  const loadLazyData = () => {
    setLoading(true);
    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }
    loadLazyTimeout = setTimeout(() => {
      let query = window.App.transformQueryParams(lazyParams)
      axios({
        method: 'get',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation" + query,
        params: {},
        headers
      })
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setAccommodation(response.data.data.items);
          setLoading(false);
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
    }, Math.random() * 1000 + 250);
  }

  const getFirstFilledValue = (sources, candidates) => {
    for (const source of sources) {
      if (!source) continue;
      for (const key of candidates) {
        const value = source[key];
        if (value === null || value === undefined) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        return value;
      }
    }
    return '';
  };

  const getHotelConnectValue = (rowData, keys = []) => getFirstFilledValue([rowData], keys);

  const getHotelConnectStatusMeta = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
    const neutralStyle = {backgroundColor: '#dddddd', color: '#5f656b'};
    if (normalizedStatus === 'success') {
      return {badgeClass: 'p-badge-success', label: t('hotelconnect_status_success'), style: {}};
    }
    if (normalizedStatus === 'failed') {
      return {badgeClass: '', label: t('hotelconnect_status_failed'), style: {backgroundColor: '#f7a2a2', color: '#5f656b'}};
    }
    if (normalizedStatus === 'skipped') {
      return {badgeClass: 'p-badge-secondary', label: t('hotelconnect_status_skipped'), style: {}};
    }
    return {badgeClass: '', label: t('hotelconnect_status_not_started'), style: neutralStyle};
  };

  const formatDateTimeValue = (value) => {
    if (!value) {
      return t('hotelconnect_certificate_not_uploaded_yet');
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return String(value);
    }
    return parsedDate.toLocaleString();
  };

  const hasAccommodationHuRecord = (rowData) => Boolean(
    rowData?.accommodation_hus?.[0]?.id
    || rowData?.accommodation_hu?.id
    || rowData?.accommodation_hu_id
    || getHotelConnectValue(rowData, ['hotel_connect_accommodation_id', 'hotelConnectAccommodationId'])
  );

  const stopSyncPollingForId = (accommodationId) => {
    if (syncPollTimeoutsRef.current[accommodationId]) {
      clearTimeout(syncPollTimeoutsRef.current[accommodationId]);
      delete syncPollTimeoutsRef.current[accommodationId];
    }
  };

  const startSyncPollingForId = async (accommodationId, attempt = 0) => {
    const maxAttempts = 12;
    const pollIntervalMs = 5000;
    try {
      const [accommodationResponse, accommodationHuResponse] = await Promise.all([
        axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation/" + accommodationId,
          params: {},
          headers
        }),
        axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation-hu/" + accommodationId,
          params: {},
          headers
        })
      ]);
      const source = {
        ...(accommodationResponse?.data?.data || {}),
        ...(accommodationHuResponse?.data?.data || {})
      };
      const registrationStatus = String(getFirstFilledValue([source], ['hotel_connect_registration_status', 'hotelConnectRegistrationStatus']) || '').toLowerCase();
      const csrStatus = String(getFirstFilledValue([source], ['hotel_connect_csr_status', 'hotelConnectCsrStatus']) || '').toLowerCase();
      const certificateStatus = String(getFirstFilledValue([source], ['hotel_connect_certificate_status', 'hotelConnectCertificateStatus']) || '').toLowerCase();
      const finalStates = ['success', 'failed', 'skipped'];
      const isFinished = finalStates.includes(registrationStatus)
        && finalStates.includes(csrStatus)
        && (certificateStatus === '' || finalStates.includes(certificateStatus));

      if (isFinished || attempt >= maxAttempts) {
        stopSyncPollingForId(accommodationId);
        loadLazyData();
        return;
      }
    } catch (error) {
      if (attempt >= maxAttempts) {
        stopSyncPollingForId(accommodationId);
        loadLazyData();
        return;
      }
    }

    syncPollTimeoutsRef.current[accommodationId] = setTimeout(() => {
      startSyncPollingForId(accommodationId, attempt + 1);
    }, pollIntervalMs);
  };

  const handleHotelConnectSyncRetry = async (rowData) => {
    const isHuCountry = String(rowData?.country || '').toUpperCase() === 'HU';
    if (!rowData?.id || (!isHuCountry && !hasAccommodationHuRecord(rowData))) {
      return;
    }
    const accommodationId = rowData.id;
    try {
      setHotelConnectSyncLoadingById((prev) => ({...prev, [accommodationId]: true}));
      await axios({
        method: 'post',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "csr/" + accommodationId + "/hotelconnect-sync",
        params: {},
        headers
      });
      window.App.toastShow('info', t('info'), t('hotelconnect_sync_started_message'));
      startSyncPollingForId(accommodationId, 0);
      loadLazyData();
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setHotelConnectSyncLoadingById((prev) => ({...prev, [accommodationId]: false}));
    }
  };
  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    setLazyParams(_lazyParams);
  }
  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: 10, value: 10},
        {label: 20, value: 20},
        {label: 50, value: 50},
        {label: 200, value: 200}
      ];

      return (
        <>
          {/*<span className="p-mx-1" style={{color: 'var(--text-color)', accommodationSelect: 'none'}}>Tételek egy oldalon: </span>*/}
          <Dropdown value={options.value} options={dropdownOptions} onChange={(e) => setRows(e.target.value)}
                    />
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', accommodationSelect: 'none', width: '120px', textAlign: 'center'}}>
                        {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  const actionBodyTemplate = (rowData) => {
    const isSyncLoading = Boolean(hotelConnectSyncLoadingById[rowData.id]);
    const canRetrySync = hasAccommodationHuRecord(rowData) && !rowData.deleted_at_snapshot;

    return (
      <React.Fragment>
        <Button icon="pi pi-eye" className="p-button-sm p-button-secondary p-mr-2"
                tooltip={t('show')}
                tooltipOptions={{position: 'left'}}
                disabled={rowData.deleted_at_snapshot}
                onClick={() => {
                  navigate('/accommodation/read/' + rowData.id)
                }}/>
        <Button icon="pi pi-pencil" className="p-button-sm  p-button-warning p-mr-2"
                tooltip={t('edit')}
                disabled={rowData.deleted_at_snapshot}
                tooltipOptions={{position: 'left'}}
                onClick={() => {
                  navigate('/accommodation/update/' + rowData.id)
                }}/>
        {canRetrySync &&
          <Button icon="pi pi-refresh"
                  className="p-button-sm p-button-info p-mr-2"
                  tooltip={t('hotelconnect_sync_retry_label')}
                  tooltipOptions={{position: 'left'}}
                  loading={isSyncLoading}
                  disabled={isSyncLoading}
                  onClick={() => {
                    handleHotelConnectSyncRetry(rowData);
                  }}/>
        }
        {rowData && rowData.deleted_at_snapshot ? (
          <Button icon="pi pi-undo" className="p-button-sm p-button-success"
                  tooltip={t('restore')}
                  tooltipOptions={{position: 'left'}}
                  onClick={() => {
                    axios({
                      method: 'get',
                      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation/" + rowData.id + "/restore",
                      params: {},
                      headers: {
                        'Authorization': 'Bearer ' + Cookies.get("userToken"),
                        "Sys-name": Cookies.get("instanceId"),
                        "Preferred-locale": Cookies.get("language")
                      }
                    })
                      .then(response => {
                        window.App.toastShow('success', t('success_restore'),);
                        loadLazyData();
                      })
                      .catch(error => {
                        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
                      })
                  }}
          />
        ) : (
          <>
            <Button icon="pi pi-trash" className="p-button-sm  p-button-danger"
                    tooltip={t('delete')}
                    tooltipOptions={{position: 'left'}}
                    onClick={() => {
                      window.App.setState({[`confirmDelete${rowData.id}`]: true})
                    }}
            />
            <ConfirmDialog group="declarative" visible={window.App.state[`confirmDelete${rowData.id}`]}
                           acceptLabel={t('yes')} rejectLabel={t('no')}
                           onHide={() => {
                             window.App.setState({[`confirmDelete${rowData.id}`]: false})
                           }}
                           message={t('confirm_delete')} header={t('confirm_delete')} icon="pi pi-exclamation-triangle"
                           accept={() => {
                             window.App.delete('accommodation', rowData.id)
                               .then(response => {
                                 window.App.toastShow('success', t('success_delete'),);
                                 if(rowData.id == localStorage.getItem('selectedAccommodation')) {
                                   localStorage.removeItem('selectedAccommodation')
                                 }
                                 loadLazyData();
                               })
                               .catch(error => {
                                 window.App.toastShow('error', t('error'), error.toString(), '', error.response);
                               })
                           }}
                           reject={() => {
                             window.App.setState({[`confirmDelete${rowData.id}`]: false})
                           }}/>
          </>
        )}
      </React.Fragment>
    );
  }

  addLocale('default', {
    clear: t('filter_clear'),
    apply: t('filter_apply'),
  });
  locale('default');

  useEffect(() => {
    if (lazyParams.filters) {
      document.getElementById('search').value = '';
      localStorage.setItem('accommodation_filters', JSON.stringify(lazyParams.filters))
      if (lazyParams.sortField) {
        localStorage.setItem('accommodation_sortField', lazyParams.sortField)
      }
      if (lazyParams.sortOrder) {
        localStorage.setItem('accommodation_sortOrder', lazyParams.sortOrder)
      }
      if (lazyParams.first >= 0) {
        localStorage.setItem('accommodation_first', lazyParams.first)
      }
      if (lazyParams.page >= 0) {
        localStorage.setItem('accommodation_page', lazyParams.page)
      }
      if (lazyParams.rows) {
        localStorage.setItem('accommodation_rows', lazyParams.rows)
        setRows(lazyParams.rows)
      }
    }
    loadLazyData();
  }, [lazyParams, window.App.state.rerenderAccommodationList])

  useEffect(() => {
    const huAccommodationIds = (accommodation || [])
      .filter((item) => String(item?.country || '').toUpperCase() === 'HU' && item?.id)
      .map((item) => item.id);

    if (!huAccommodationIds.length) {
      setHotelConnectRegistrationStatusByAccommodationId({});
      setHotelConnectRegistrationStatusLoadingByAccommodationId({});
      return;
    }

    let isCancelled = false;
    const nextLoadingState = {};
    huAccommodationIds.forEach((accommodationId) => {
      nextLoadingState[accommodationId] = true;
    });
    setHotelConnectRegistrationStatusLoadingByAccommodationId(nextLoadingState);
    setHotelConnectRegistrationStatusByAccommodationId({});

    Promise.all(
      huAccommodationIds.map((accommodationId) =>
        axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation-hu/" + accommodationId,
          params: {},
          headers
        })
          .then((response) => {
            const huData = response?.data?.data || response?.data || {};
            const status = getFirstFilledValue([huData], ['hotel_connect_registration_status', 'hotelConnectRegistrationStatus']);
            return {accommodationId, status};
          })
          .catch(() => ({accommodationId, status: ''}))
      )
    ).then((results) => {
      if (isCancelled) {
        return;
      }
      const statusById = {};
      results.forEach((result) => {
        statusById[result.accommodationId] = result.status;
      });
      setHotelConnectRegistrationStatusByAccommodationId(statusById);
      setHotelConnectRegistrationStatusLoadingByAccommodationId({});
    });

    return () => {
      isCancelled = true;
    };
  }, [accommodation]);

  useEffect(() => {
    return () => {
      Object.keys(syncPollTimeoutsRef.current).forEach((accommodationId) => {
        clearTimeout(syncPollTimeoutsRef.current[accommodationId]);
      });
      syncPollTimeoutsRef.current = {};
    };
  }, []);

  const shouldShowHotelConnectNtakSyncColumn = (accommodation || []).some(
    (item) => String(item?.country || '').toUpperCase() === 'HU'
  );

  return (
    <>
      <div className={"p-grid"}>
        <div className={"p-col-12 p-lg-4 p-col-align-center"}>
          <h2 className={"p-m-0"}>{t('accommodation_list')}</h2>
        </div>
        <div className={"p-col p-text-right p-col-align-center"}>
          <HelpComponent type={"site"} position={"UNDER_HELP_BUTTON"}/>
          {Cookies.get('roles') && Cookies.get('roles').toString().includes('ROLE_SUPER_ADMIN') &&
            <Button type="button"
                    label={t('accommodation_import')}
                    icon={"pi pi-upload"}
                    onClick={() => {
                      navigate('/accommodation/all/import/accommodation')
                    }}
                    className="p-button-secondary p-ml-2"/>
          }
          {localStorage.getItem('accommodation_filters') &&
            <a style={{color: "red"}} className={"p-button p-button-danger p-ml-2"}
               onClick={() => {
                 localStorage.removeItem('accommodation_filters')
                 localStorage.removeItem('accommodation_sortField')
                 localStorage.removeItem('accommodation_sortOrder')
                 localStorage.removeItem('accommodation_rows')
                 localStorage.removeItem('accommodation_page')
                 localStorage.removeItem('accommodation_first')
                 setLazyParams({
                   ...lazyParams,
                   filters: null
                 })
               }}
            >
              <i className={"pi pi-exclamation-triangle"}></i>&nbsp;{t('clear_filter')}
            </a>
          }
          {lazyParams && lazyParams.is_active === 0 ? (
            <Button type="button" icon={"pi pi-eye-slash"}
                    label={t('hide_deleted')}
                    onClick={() => {
                      let _lazyParams = lazyParams
                      delete lazyParams.is_active
                      setLazyParams(_lazyParams)
                      loadLazyData()
                    }}
                    className="p-button-danger p-ml-2"/>
          ) : (
            <Button type="button" icon={"pi pi-eye"}
                    label={t('show_deleted')}
                    onClick={() => {
                      setLazyParams({
                        ...lazyParams,
                        is_active: 0
                      })
                    }}
                    className="p-button-success p-ml-2"/>
          )}
          <span className="p-input-icon-left p-ml-2">
              <i className="pi pi-search"/>
              <InputText id="search"
                         placeholder={t('search')}
                         onChange={(e) => {
                           clearTimeout(window.searchTimeout)
                           window.searchTimeout = setTimeout(function () {
                             localStorage.removeItem('accommodation_filters')
                             localStorage.removeItem('accommodation_sortField')
                             localStorage.removeItem('accommodation_sortOrder')
                             localStorage.removeItem('accommodation_rows')
                             localStorage.removeItem('accommodation_page')
                             localStorage.removeItem('accommodation_first')
                             setLazyParams({
                               ...lazyParams,
                               filters: null,
                               genericSearch: e.target.value
                             })
                           }, 1000)
                         }}
                         className={""}
              />
            </span>
          <Button type="button"
                  label={t('new_accommodation')}
                  icon={"pi pi-plus"}
                  onClick={() => {
                    navigate('/accommodation/create')
                  }}
                  className="p-button-success p-ml-2"/>
        </div>
      </div>
      {/* <HelpComponent type={"list_box"} box={"accommodation"}/> */}
      <DataTable
        ref={dt}
        size={"small"}
        emptyMessage={t('empty_message')}
        value={accommodation} paginator first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
        onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
        onFilter={onFilter} filters={lazyParams.filters} loading={loading} lazy
        paginatorTemplate={pager} rows={rows} emptyMessage={t('empty_message')}
        autoLayout={true}
        filterDisplay="menu"
        responsiveLayout={window.App.state.responsiveLayout}
        breakpoint="1365px"
        stripedRows
        dataKey="id"
        selectionMode={"single"}
        onRowDoubleClick={(rowData) => {
          if(!rowData.data.deleted_at_snapshot)
          navigate('/accommodation/update/' + rowData.data.id)
        }}
      >
        
        <Column field="name" sortable header={t('name')}
                filter
                filterMatchMode="like"
                filterMatchModeOptions={filterTextMatchModes}
                body={(rowData) => <><span className={"p-text-nowrap"}>{rowData?.name}<span className={"tt-auto-invoice p-ml-2"} data-pr-tooltip={rowData?.auto_invoice_on_check_out === 'CREATE_AND_SYNC' ? t('auto_invoice_tooltip_active') : t('auto_invoice_tooltip_inactive')}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={"auto-invoice-icon"} style={{ color: rowData?.auto_invoice_on_check_out === 'CREATE_AND_SYNC' ? '#0abb87' : '#dddddd', position: 'relative', bottom: '-3px' }}><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8" /><path d="M5 21l2.5-1.5L10 21l2.5-1.5L14 21l1.5-1" /><path d="M9 7h6" /><path d="M9 11h4" /><path d="M18 14a3 3 0 1 0 3 3" /><path d="M21 20v-3h-3" /></svg></span></span><Tooltip target=".tt-auto-invoice"/></>}
        ></Column>
        <Column field="zip_code" sortable header={t('zip_code')}
                filter
                filterMatchMode="like"
                filterMatchModeOptions={filterTextMatchModes}
        ></Column>
        <Column field="city" sortable header={t('city')}
                filter
                filterMatchMode="like"
                filterMatchModeOptions={filterTextMatchModes}
        ></Column>
        <Column field="street_and_number" sortable header={t('street_and_number')}
                filter
                filterMatchMode="like"
                filterMatchModeOptions={filterTextMatchModes}
        ></Column>
        <Column field="currency_code" sortable header={t('currency')}
                filter
                filterMatchMode="like"
                filterMatchModeOptions={filterTextMatchModes}
        ></Column>
        <Column field="channel_manager" sortable header={t('channel_manager')}
                filter
                filterMatchMode="like"
                filterMatchModeOptions={filterTextMatchModes}
        ></Column>
        {shouldShowHotelConnectNtakSyncColumn &&
          <Column field="hotelConnectRegistrationStatus"
                  header={t('hotelconnect_ntak_sync_label')}
                  className={"p-text-center p-text-nowrap"}
                  body={(rowData) => {
                    if (String(rowData?.country || '').toUpperCase() !== 'HU') {
                      return '';
                    }
                    if (hotelConnectRegistrationStatusLoadingByAccommodationId[rowData?.id]) {
                      return <i className={"pi pi-spin pi-spinner"}></i>;
                    }
                    const status = hotelConnectRegistrationStatusByAccommodationId[rowData?.id];
                    const meta = getHotelConnectStatusMeta(status);
                    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
                    const isFailed = normalizedStatus === 'failed';
                    const isSyncLoading = Boolean(hotelConnectSyncLoadingById[rowData?.id]);
                    const canRetry = isFailed && !rowData?.deleted_at_snapshot;
                    return (
                      <div className={"p-d-flex p-ai-center p-jc-center"}>
                        <span
                          className={['p-badge', 'p-d-block', 'w-100', meta.badgeClass, `tt-hotelconnect-retry-accommodation-list-${rowData?.id}`].join(' ')}
                          data-pr-tooltip={canRetry ? t('hotelconnect_sync_retry_label') : undefined}
                          role={canRetry ? 'button' : undefined}
                          tabIndex={canRetry ? (isSyncLoading ? -1 : 0) : undefined}
                          style={{
                            ...meta.style,
                            cursor: canRetry && !isSyncLoading ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (!canRetry || isSyncLoading) return;
                            handleHotelConnectSyncRetry(rowData);
                          }}
                          onKeyDown={(event) => {
                            if (!canRetry || isSyncLoading) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleHotelConnectSyncRetry(rowData);
                            }
                          }}
                        >
                          <span>{meta.label}</span>
                          {canRetry &&
                            <>
                              <i
                                className={`p-ml-1 pi ${isSyncLoading ? 'pi-spin pi-spinner' : 'pi-refresh'}`}
                                style={{color: 'inherit', fontSize: '0.8rem'}}
                              ></i>
                              <Tooltip target={`.tt-hotelconnect-retry-accommodation-list-${rowData?.id}`}/>
                            </>
                          }
                        </span>
                      </div>
                    );
                  }}>
          </Column>
        }
        {/*<Column field="hotel_connect_accommodation_id" header={t('hotelconnect_accommodation_id_label')}*/}
        {/*        body={(rowData) => getHotelConnectValue(rowData, ['hotel_connect_accommodation_id', 'hotelConnectAccommodationId']) || '-'}*/}
        {/*></Column>*/}
        {/*<Column field="hotel_connect_csr_status" header={t('hotelconnect_csr_status_label')}*/}
        {/*        body={(rowData) => {*/}
        {/*          const status = getHotelConnectValue(rowData, ['hotel_connect_csr_status', 'hotelConnectCsrStatus']);*/}
        {/*          const meta = getHotelConnectStatusMeta(status);*/}
        {/*          return <span className={['p-badge', meta.badgeClass].join(' ')}>{meta.label}</span>;*/}
        {/*        }}*/}
        {/*></Column>*/}
        {/*<Column field="hotel_connect_certificate_status" header={t('hotelconnect_certificate_status_label')}*/}
        {/*        body={(rowData) => {*/}
        {/*          const status = getHotelConnectValue(rowData, ['hotel_connect_certificate_status', 'hotelConnectCertificateStatus']);*/}
        {/*          const meta = getHotelConnectStatusMeta(status);*/}
        {/*          return <span className={['p-badge', meta.badgeClass].join(' ')}>{meta.label}</span>;*/}
        {/*        }}*/}
        {/*></Column>*/}
        {/*<Column field="hotel_connect_certificate_uploaded_at" header={t('hotelconnect_certificate_uploaded_at_label')}*/}
        {/*        body={(rowData) => {*/}
        {/*          const value = getHotelConnectValue(rowData, ['hotel_connect_certificate_uploaded_at', 'hotelConnectCertificateUploadedAt']);*/}
        {/*          return formatDateTimeValue(value);*/}
        {/*        }}*/}
        {/*></Column>*/}
        {/*<Column header={t('hotelconnect_sync_status_hint_label')}*/}
        {/*        body={(rowData) => {*/}
        {/*          const registrationStatus = String(getHotelConnectValue(rowData, ['hotel_connect_registration_status', 'hotelConnectRegistrationStatus']) || '').toLowerCase();*/}
        {/*          const csrStatus = String(getHotelConnectValue(rowData, ['hotel_connect_csr_status', 'hotelConnectCsrStatus']) || '').toLowerCase();*/}
        {/*          const certificateStatus = String(getHotelConnectValue(rowData, ['hotel_connect_certificate_status', 'hotelConnectCertificateStatus']) || '').toLowerCase();*/}
        {/*          if (hotelConnectSyncInProgressById[rowData.id]) {*/}
        {/*            return <span className="p-text-bold">{t('hotelconnect_sync_in_progress')}</span>;*/}
        {/*          }*/}
        {/*          if (registrationStatus === 'success' && csrStatus === 'success') {*/}
        {/*            return <span style={{color: '#2e7d32'}}>{t('hotelconnect_csr_ready_hint')}</span>;*/}
        {/*          }*/}
        {/*          if ([registrationStatus, csrStatus, certificateStatus].includes('failed')) {*/}
        {/*            return <span style={{color: '#c62828'}}>{t('hotelconnect_sync_failed_warning')}</span>;*/}
        {/*          }*/}
        {/*          return '';*/}
        {/*        }}*/}
        {/*></Column>*/}
        <Column field="auto_invoice" sortable header={t('auto_invoice_on_check_out')}
                filter
                filterMatchMode="like"
                filterMatchModeOptions={filterTextMatchModes}
                body={(rowData) => rowData.auto_invoice ? t(rowData.auto_invoice.toLowerCase()) : ''}
        ></Column>
        <Column field="created_at_snapshot" sortable header={t('created_at')}
                filter
                filterField={"created_at"}
                sortField={"created_at"}
                filterMatchMode="between"
                filterMatchModeOptions={filterDateMatchModes}
                body={(rowData) => {
                  if (rowData.created_at_snapshot !== null) {
                    return (
                      <span>{moment.utc(rowData.created_at_snapshot).local().format(t('YYYY-MM-DD HH:mm:ss'))}</span>
                    )
                  }
                }}></Column>
        <Column field="updated_at_snapshot" sortable header={t('updated_at')}
                filterField={"updated_at"}
                sortField={"updated_at"}
                filter
                filterMatchMode="between"
                filterMatchModeOptions={filterDateMatchModes}
                body={(rowData) => {
                  if (rowData.updated_at_snapshot !== null) {
                    return (
                      <span>{moment.utc(rowData.updated_at_snapshot).local().format(t('YYYY-MM-DD HH:mm:ss'))}</span>
                    )
                  }
                }}></Column>
        <Column body={actionBodyTemplate} className={"p-py-0 p-text-nowrap p-text-right"}></Column>
      </DataTable>
    </>
  )
}
export default withTranslation('common')(_FM_AccommodationListComponent);
